import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SdkError } from 'wg-api-sdk';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Switch } from '@/components/shadcn/switch';
import { Textarea } from '@/components/shadcn/textarea';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import type { Party } from '@/features/party/types';

import useActionsBrio from '../../hooks/useActionsBrio';
import useEnsureEmailDocument from '../../hooks/useEnsureEmailDocument';
import PriorityField from '../PriorityField';

import type { ExportEmailValuesBrio } from './types';

type Props = ModalProps & {
  defaultValues?: Partial<Omit<ExportEmailValuesBrio, 'priority'>>;
  party: Maybe<Party>;
};

const CRM_KEY = 'brio';

export default function ExportEmailModalBrio({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();

  const { email } = useEmail();
  const ensureEmailDocument = useEnsureEmailDocument();

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    watch,
  } = useForm<ExportEmailValuesBrio>({
    defaultValues: {
      createTask: true,
      description: defaultValues?.description,
      priority: 'NORMAL',
      date: defaultValues?.date ? new Date(defaultValues.date) : new Date(),
    },
    shouldUnregister: true,
  });

  const brioActions = useActionsBrio();
  const { getSelectedMessageId } = useEmailClient();

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const createTask = watch('createTask');

  const onSubmit: SubmitHandler<ExportEmailValuesBrio> = async (data) => {
    try {
      if (!crmPartyId) {
        throw new Error('Missing crmPartyId when exporting to brio');
      }

      const { filename, link } = await ensureEmailDocument();

      await brioActions.uploadDocument({
        crmPartyId,
        filename,
        link,
        description: email?.subject ?? '',
      });

      if (createTask) {
        await brioActions.createTask({
          description: data.description,
          priority: data.priority,
          dueDate: format(data.date, 'yyyy-MM-dd'),
          isPersonalNote: false,
          isVisibleInMyBroker: false,
          crmPartyId,
        });
      }

      posthog.capture('Email exported to CRM', {
        crmBackend: CRM_KEY,
        partyId: party?.id,
        messageId: getSelectedMessageId(),
        withTask: Boolean(createTask),
      });
      toast.success(t('EMAIL_EXPORT_SUCCESS'));
      onClose();
    } catch (error) {
      toast.error(t('EMAIL_EXPORT_ERROR'), {
        description: error instanceof SdkError ? error.message : undefined,
      });
      Sentry.captureException(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <div className="mb-3">
          <Controller
            control={control}
            name="createTask"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Switch
                  name="createTask"
                  id="createTask"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />

                <FieldLabel htmlFor="createTask" className="cursor-pointer">
                  {t('EXPORT_EMAIL_CREATE_TASK_LABEL')}
                </FieldLabel>
              </Field>
            )}
          />
        </div>

        {createTask && (
          <>
            <Field>
              <FieldLabel htmlFor="description">
                {t('TASK_FIELD_DESCRIPTION')}
              </FieldLabel>
              <Textarea
                {...register('description', { required: createTask })}
                placeholder={t('TASK_FIELD_DESCRIPTION_PH')}
                className="h-48"
              />
            </Field>

            <PriorityField<ExportEmailValuesBrio>
              control={control}
              priorities={[
                { label: t('PRIORITY_BRIO_LOW'), value: 'LOW' },
                { label: t('PRIORITY_BRIO_NORMAL'), value: 'NORMAL' },
                { label: t('PRIORITY_BRIO_HIGH'), value: 'HIGH' },
              ]}
            />

            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('TASK_FIELD_END_DATE_BRIO')}</FieldLabel>
                  <DatePicker {...field} />
                </Field>
              )}
            />
          </>
        )}
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting} disabled={!isValid}>
          {createTask
            ? t('EXPORT_EMAIL_AND_CREATE_TASK_SUBMIT_BUTTON')
            : t('EXPORT_EMAIL_SUBMIT_BUTTON')}
        </SubmitButton>
      </div>
    </form>
  );
}
